import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { API, useAuth } from '../../App';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Checkbox } from '../../components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Switch } from '../../components/ui/switch';
import { toast } from 'sonner';
import { Users, Mail, Phone, MapPin, Clock, RefreshCw, Crown, Shield, UserPlus, Plus, Trash2, Eye, EyeOff } from 'lucide-react';

const AdminVolunteers = () => {
  const { token, user } = useAuth();
  const [volunteers, setVolunteers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [promoteDialogOpen, setPromoteDialogOpen] = useState(false);
  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [selectedVolunteer, setSelectedVolunteer] = useState(null);
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [promoting, setPromoting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    address: '',
    skills: '',
    availability: '',
    create_account: false,
    password: ''
  });

  const fetchVolunteers = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/volunteers`, { headers: { Authorization: `Bearer ${token}` } });
      setVolunteers(response.data);
    } catch (error) {
      console.error('Failed to fetch:', error);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    fetchVolunteers();
  }, [fetchVolunteers]);

  const handlePromoteClick = (volunteer) => {
    setSelectedVolunteer(volunteer);
    setIsSuperAdmin(false);
    setPromoteDialogOpen(true);
  };

  const handlePromote = async () => {
    if (!selectedVolunteer) return;
    
    setPromoting(true);
    try {
      const response = await axios.post(
        `${API}/volunteers/${selectedVolunteer.id}/promote?is_super_admin=${isSuperAdmin}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      if (response.data.temp_password) {
        toast.success(`${selectedVolunteer.name} promoted to ${isSuperAdmin ? 'Super Admin' : 'Admin'}! Temporary password: ${response.data.temp_password}`, {
          duration: 10000
        });
      } else {
        toast.success(`${selectedVolunteer.name} promoted to ${isSuperAdmin ? 'Super Admin' : 'Admin'}!`);
      }
      
      setPromoteDialogOpen(false);
      fetchVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to promote volunteer');
    } finally {
      setPromoting(false);
    }
  };

  const handleAddVolunteer = async () => {
    if (!formData.name || !formData.email || !formData.phone || !formData.availability) {
      toast.error('Please fill in all required fields');
      return;
    }
    if (formData.create_account && !formData.password) {
      toast.error('Password is required when creating an account');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        address: formData.address || null,
        skills: formData.skills ? formData.skills.split(',').map(s => s.trim()) : [],
        availability: formData.availability,
        create_account: formData.create_account,
        password: formData.create_account ? formData.password : null
      };

      const response = await axios.post(`${API}/admin/volunteers`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.data.account_created) {
        toast.success(`Volunteer added with login account! Email: ${formData.email}`);
      } else {
        toast.success('Volunteer added successfully!');
      }

      setAddDialogOpen(false);
      setFormData({
        name: '',
        email: '',
        phone: '',
        address: '',
        skills: '',
        availability: '',
        create_account: false,
        password: ''
      });
      fetchVolunteers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add volunteer');
    } finally {
      setSubmitting(false);
    }
  };

  const handleDeleteVolunteer = async (volunteerId) => {
    if (!confirm('Are you sure you want to remove this volunteer?')) return;
    
    try {
      await axios.delete(`${API}/admin/volunteers/${volunteerId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Volunteer removed');
      fetchVolunteers();
    } catch (error) {
      toast.error('Failed to remove volunteer');
    }
  };

  const canManageAdmins = user?.is_super_admin || user?.permissions?.manage_admins;

  return (
    <div className="p-4 sm:p-6 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading text-xl sm:text-2xl font-bold text-slate-900 flex items-center gap-2">
            <Users className="w-5 h-5 sm:w-6 sm:h-6 text-purple-500" /> Volunteers
          </h1>
          <p className="text-sm sm:text-base text-slate-600">View, add, and manage volunteer sign-ups</p>
        </div>
        <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
          <DialogTrigger asChild>
            <Button className="bg-purple-600 hover:bg-purple-700 self-start sm:self-auto" data-testid="add-volunteer-btn">
              <Plus className="w-4 h-4 mr-2" /> Add Volunteer
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle className="font-heading flex items-center gap-2">
                <UserPlus className="w-5 h-5 text-purple-500" />
                Add New Volunteer
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <Label>Full Name *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData(p => ({ ...p, name: e.target.value }))}
                    placeholder="Enter name"
                    data-testid="volunteer-name-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Email *</Label>
                  <Input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData(p => ({ ...p, email: e.target.value }))}
                    placeholder="email@example.com"
                    data-testid="volunteer-email-input"
                  />
                </div>
                <div>
                  <Label>Phone *</Label>
                  <Input
                    value={formData.phone}
                    onChange={(e) => setFormData(p => ({ ...p, phone: e.target.value }))}
                    placeholder="242-XXX-XXXX"
                  />
                </div>
                <div>
                  <Label>Address</Label>
                  <Input
                    value={formData.address}
                    onChange={(e) => setFormData(p => ({ ...p, address: e.target.value }))}
                    placeholder="Killarney area"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Availability *</Label>
                  <Input
                    value={formData.availability}
                    onChange={(e) => setFormData(p => ({ ...p, availability: e.target.value }))}
                    placeholder="e.g., Weekends, Evenings"
                  />
                </div>
                <div className="col-span-2">
                  <Label>Skills (comma-separated)</Label>
                  <Input
                    value={formData.skills}
                    onChange={(e) => setFormData(p => ({ ...p, skills: e.target.value }))}
                    placeholder="e.g., Driving, Cooking, First Aid"
                  />
                </div>
              </div>

              {/* Create Account Option */}
              <div className="p-4 bg-purple-50 rounded-lg space-y-3">
                <div className="flex items-center gap-3">
                  <Checkbox
                    id="create-account"
                    checked={formData.create_account}
                    onCheckedChange={(c) => setFormData(p => ({ ...p, create_account: c }))}
                  />
                  <label htmlFor="create-account" className="text-sm font-medium cursor-pointer">
                    Create login account for this volunteer
                  </label>
                </div>
                
                {formData.create_account && (
                  <div>
                    <Label>Password *</Label>
                    <div className="relative">
                      <Input
                        type={showPassword ? 'text' : 'password'}
                        value={formData.password}
                        onChange={(e) => setFormData(p => ({ ...p, password: e.target.value }))}
                        placeholder="Enter password"
                        data-testid="volunteer-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400"
                      >
                        {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                      </button>
                    </div>
                    <p className="text-xs text-purple-600 mt-1">
                      This will allow them to log in and access their dashboard
                    </p>
                  </div>
                )}
              </div>

              <Button
                onClick={handleAddVolunteer}
                disabled={submitting}
                className="w-full bg-purple-600 hover:bg-purple-700"
                data-testid="save-volunteer-btn"
              >
                {submitting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Plus className="w-4 h-4 mr-2" />}
                Add Volunteer
              </Button>
            </div>
          </DialogContent>
        </Dialog>
      </div>

      <Card className="bg-plp-blue/10 border-plp-blue/20">
        <CardContent className="py-4">
          <p className="text-plp-blue font-heading font-bold text-2xl">{volunteers.length}</p>
          <p className="text-slate-600">Total Volunteers</p>
        </CardContent>
      </Card>

      {loading ? (
        <div className="flex justify-center py-12"><RefreshCw className="w-8 h-8 animate-spin text-plp-blue" /></div>
      ) : volunteers.length === 0 ? (
        <Card className="bg-white"><CardContent className="py-12 text-center"><Users className="w-12 h-12 text-slate-300 mx-auto mb-4" /><p className="text-slate-500">No volunteers yet</p></CardContent></Card>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {volunteers.map((v) => (
            <Card key={v.id} className="bg-white" data-testid={`volunteer-card-${v.id}`}>
              <CardContent className="p-4">
                <div className="flex items-start justify-between">
                  <h3 className="font-heading font-bold">{v.name}</h3>
                  <div className="flex gap-1">
                    {canManageAdmins && (
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handlePromoteClick(v)}
                        className="text-purple-600 hover:bg-purple-50"
                        data-testid={`promote-volunteer-${v.id}`}
                      >
                        <Crown className="w-4 h-4" />
                      </Button>
                    )}
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleDeleteVolunteer(v.id)}
                      className="text-red-500 hover:bg-red-50"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                <div className="mt-2 space-y-1 text-sm text-slate-600">
                  <p className="flex items-center gap-2"><Mail className="w-4 h-4 text-slate-400" />{v.email}</p>
                  <p className="flex items-center gap-2"><Phone className="w-4 h-4 text-slate-400" />{v.phone}</p>
                  {v.address && <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-slate-400" />{v.address}</p>}
                  <p className="flex items-center gap-2"><Clock className="w-4 h-4 text-slate-400" />{v.availability}</p>
                </div>
                {v.skills?.length > 0 && (
                  <div className="mt-3 flex flex-wrap gap-1">
                    {v.skills.map(s => <Badge key={s} variant="outline" className="text-xs">{s}</Badge>)}
                  </div>
                )}
                <p className="text-xs text-slate-400 mt-3">Signed up: {new Date(v.created_at).toLocaleDateString()}</p>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Promote Dialog */}
      <Dialog open={promoteDialogOpen} onOpenChange={setPromoteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="font-heading flex items-center gap-2">
              <UserPlus className="w-5 h-5 text-purple-500" />
              Promote to Admin
            </DialogTitle>
          </DialogHeader>
          {selectedVolunteer && (
            <div className="space-y-6 mt-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="font-medium">{selectedVolunteer.name}</p>
                <p className="text-sm text-slate-500">{selectedVolunteer.email}</p>
              </div>
              
              <div className="p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Crown className={`w-5 h-5 ${isSuperAdmin ? 'text-plp-gold' : 'text-slate-400'}`} />
                    <div>
                      <Label className="text-base">Super Admin</Label>
                      <p className="text-xs text-slate-500">Has all permissions including managing other admins</p>
                    </div>
                  </div>
                  <Switch
                    checked={isSuperAdmin}
                    onCheckedChange={setIsSuperAdmin}
                    data-testid="super-admin-toggle"
                  />
                </div>
              </div>

              <div className="p-4 bg-amber-50 rounded-lg text-sm">
                <p className="font-medium text-amber-800">Note:</p>
                <ul className="text-amber-700 mt-1 space-y-1">
                  <li>• A new admin account will be created with a temporary password</li>
                  <li>• The volunteer should change their password on first login</li>
                  <li>• {isSuperAdmin ? 'Super Admins can manage all aspects of the site' : 'Regular Admins have limited permissions'}</li>
                </ul>
              </div>

              <div className="flex gap-3">
                <Button
                  variant="outline"
                  onClick={() => setPromoteDialogOpen(false)}
                  className="flex-1"
                >
                  Cancel
                </Button>
                <Button
                  onClick={handlePromote}
                  disabled={promoting}
                  className="flex-1 bg-purple-600 hover:bg-purple-700"
                  data-testid="confirm-promote-btn"
                >
                  {promoting ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Crown className="w-4 h-4 mr-2" />}
                  Promote to {isSuperAdmin ? 'Super Admin' : 'Admin'}
                </Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminVolunteers;
